import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toggleDisplayName } from '@/features/schema/format'
import { PROPERTY_TYPES } from '@/features/schema/types'
import type { PropertySchema } from '@/services/api/schemas'
import { cn } from '@/utils/cn'

export interface PropertyEditorProps {
  properties: PropertySchema[]
  onChange: (props: PropertySchema[]) => void
}

/**
 * Controlled editor for a list of property definitions. Holds no IO — it emits
 * the next property list via `onChange`.
 */
export function PropertyEditor({ properties, onChange }: PropertyEditorProps) {
  const { t } = useTranslation()
  const addProperty = () => {
    onChange([
      ...properties,
      { name: '', type: 'STRING', required: false, unique: false, is_display_name: false },
    ])
  }

  const updateProperty = (index: number, field: keyof PropertySchema, value: unknown) => {
    const updated = [...properties]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  const removeProperty = (index: number) => {
    onChange(properties.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm text-muted-foreground">
          {t('schemaAgent.editor.properties')}
        </Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={addProperty}
          className="h-9 md:h-7 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-3 w-3 mr-1" />
          {t('schemaAgent.editor.propertyEditor.add')}
        </Button>
      </div>
      {properties.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          {t('schemaAgent.editor.noProperties')}
        </p>
      ) : (
        // Below md the name takes its own line, with the type and flags under
        // it, so a long property name stays readable.
        <div className="space-y-4 md:space-y-2">
          {properties.map((prop, index) => (
            <div
              key={index}
              className="flex flex-wrap items-center gap-x-2 gap-y-1.5 md:flex-nowrap"
            >
              <Input
                value={prop.name}
                onChange={e => updateProperty(index, 'name', e.target.value)}
                placeholder={t('schemaAgent.editor.propertyEditor.namePlaceholder')}
                className="basis-full md:flex-1 h-9 md:h-8 text-sm bg-muted border-border"
              />
              <Select value={prop.type} onValueChange={v => updateProperty(index, 'type', v)}>
                <SelectTrigger className="w-32 max-md:flex-1 h-8 text-sm bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[300]">
                  {PROPERTY_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Display-name is a single-choice marker per entity type, so
                  toggling one clears it on the rest (see toggleDisplayName). */}
              <button
                type="button"
                title={t(
                  `schemaAgent.editor.propertyEditor.${prop.is_display_name ? 'displayName' : 'markDisplayName'}`
                )}
                onClick={() => onChange(toggleDisplayName(properties, index))}
                className={cn(
                  'h-9 w-9 md:h-8 md:w-8 flex items-center justify-center rounded text-xs font-bold shrink-0 border',
                  prop.is_display_name
                    ? 'bg-info-surface text-info-foreground border-info-border'
                    : 'bg-muted text-muted-foreground border-border hover:text-foreground'
                )}
              >
                {t('schemaAgent.editor.propertyEditor.displayNameShort')}
              </button>
              <button
                type="button"
                title={t(
                  `schemaAgent.editor.propertyEditor.${prop.unique ? 'unique' : 'notUnique'}`
                )}
                onClick={() => updateProperty(index, 'unique', !prop.unique)}
                className={cn(
                  'h-9 w-9 md:h-8 md:w-8 flex items-center justify-center rounded text-xs font-bold shrink-0 border',
                  prop.unique
                    ? 'bg-warning-surface text-warning-foreground border-warning-border'
                    : 'bg-muted text-muted-foreground border-border hover:text-foreground'
                )}
              >
                {t('schemaAgent.editor.propertyEditor.uniqueShort')}
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeProperty(index)}
                className="h-9 w-9 md:h-8 md:w-8 p-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
